import React, { useCallback, useEffect, useState } from 'react';

import { Box, Button, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { generatePath } from 'react-router-dom';

import {
  ANALYTICS_COLLECTION_ENABLED,
  applyConsentUpdate,
  CONSENT_OPEN_EVENT,
  type ConsentGranted,
  readStoredConsent,
  toConsentGranted,
  writeStoredConsent,
} from 'config/analyticsConfig';
import PATHS from 'constants/paths';

import { cookieConsentContent as content } from './cookieConsentContent';
import useStyles from './CookieConsentBanner.styles';

type View = 'bar' | 'settings';

const CookieConsentBanner: React.FC = () => {
  const { classes } = useStyles();
  const [visible, setVisible] = useState(false);
  const [view, setView] = useState<View>('bar');
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  const persist = useCallback((choice: ConsentGranted) => {
    writeStoredConsent(choice);
    applyConsentUpdate(choice);
    setVisible(false);
    setView('bar');
  }, []);

  const openBanner = useCallback(() => {
    const stored = readStoredConsent();
    setAnalytics(stored?.analytics ?? false);
    setAds(stored?.ads ?? false);
    setView(stored ? 'settings' : 'bar');
    setVisible(true);
  }, []);

  useEffect(() => {
    if (!ANALYTICS_COLLECTION_ENABLED) return undefined;

    if (!readStoredConsent()) {
      setVisible(true);
    }

    const onOpen = () => openBanner();
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, [openBanner]);

  if (!ANALYTICS_COLLECTION_ENABLED || !visible) {
    return null;
  }

  const privacyHref = `/${generatePath(PATHS.DOCUMENTS, { documentType: 'privacy_policy' })}`;

  return (
    <Box className={classes.root} role='dialog' aria-modal='true' aria-labelledby='cookie-consent-title'>
      <Box className={classes.panel}>
        <Typography id='cookie-consent-title' className={classes.title} component='h2'>
          {content.title}
        </Typography>
        <Typography className={classes.description}>{content.description}</Typography>
        <Typography className={classes.privacy}>
          <Link href={privacyHref} target='_blank' rel='noreferrer noopener'>
            {content.privacyLabel}
          </Link>
        </Typography>

        {view === 'settings' ? (
          <Box className={classes.settings}>
            <ConsentRow
              className={classes.row}
              titleClassName={classes.rowTitle}
              descriptionClassName={classes.rowDescription}
              textClassName={classes.rowText}
              title={content.necessaryTitle}
              description={content.necessaryDescription}
              checked
              disabled
            />
            <ConsentRow
              className={classes.row}
              titleClassName={classes.rowTitle}
              descriptionClassName={classes.rowDescription}
              textClassName={classes.rowText}
              title={content.analyticsTitle}
              description={content.analyticsDescription}
              checked={analytics}
              onChange={setAnalytics}
            />
            <ConsentRow
              className={classes.row}
              titleClassName={classes.rowTitle}
              descriptionClassName={classes.rowDescription}
              textClassName={classes.rowText}
              title={content.marketingTitle}
              description={content.marketingDescription}
              checked={ads}
              onChange={setAds}
            />
            <Box className={classes.actions}>
              <Button variant='outlined' color='inherit' onClick={() => setView('bar')}>
                {content.closeSettings}
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => persist(toConsentGranted({ analytics, ads }))}
              >
                {content.saveChoices}
              </Button>
            </Box>
          </Box>
        ) : (
          <Box className={classes.actions}>
            <Button variant='outlined' color='inherit' onClick={() => persist({ analytics: false, ads: false })}>
              {content.rejectOptional}
            </Button>
            <Button variant='outlined' color='inherit' onClick={() => setView('settings')}>
              {content.customize}
            </Button>
            <Button variant='contained' color='primary' onClick={() => persist({ analytics: true, ads: true })}>
              {content.acceptAll}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

type ConsentRowProps = {
  className: string;
  textClassName: string;
  titleClassName: string;
  descriptionClassName: string;
  title: string;
  description: string;
  checked: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
};

const ConsentRow: React.FC<ConsentRowProps> = ({
  className,
  textClassName,
  titleClassName,
  descriptionClassName,
  title,
  description,
  checked,
  onChange,
  disabled,
}) => (
  <FormControlLabel
    className={className}
    disabled={disabled}
    control={
      <Checkbox
        size='small'
        checked={checked}
        disabled={disabled}
        onChange={event => onChange?.(event.target.checked)}
        color='primary'
      />
    }
    label={
      <span className={textClassName}>
        <span className={titleClassName}>{title}</span>
        <span className={descriptionClassName}>{description}</span>
      </span>
    }
  />
);

export default CookieConsentBanner;
